import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Clapperboard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { SceneDialog } from "@/components/studio/SceneDialog";
import { api } from "@/lib/api";

export const useScenes = (uid) => useQuery({ queryKey: ["scenes", uid], queryFn: () => api.get(`/universes/${uid}/scenes`).then((r) => r.data) });

export const SceneLibrary = ({ uid }) => {
  const qc = useQueryClient();
  const { data: scenes = [] } = useScenes(uid);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const refresh = () => { qc.invalidateQueries({ queryKey: ["scenes", uid] }); qc.invalidateQueries({ queryKey: ["draft-items"] }); };
  return (
    <section data-testid="scene-library">
      <div className="mb-5 flex items-center justify-between">
        <p className="max-w-xl text-sm text-[#A8A090]">Scene notes are ideas you plan to use. Drop them onto any storyboard as placeholders to test the flow.</p>
        <Button onClick={() => setEditing({})} data-testid="new-scene-button"><Plus className="mr-1 h-4 w-4" />New scene</Button>
      </div>
      {scenes.length === 0 && <p className="rounded-md border border-dashed border-[#2D3442] p-10 text-center text-sm text-[#A8A090]" data-testid="scenes-empty">No scene notes yet.</p>}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">
        {scenes.map((s) => (
          <div key={s.id} className="group rounded-md border border-dashed border-[#8B5CF6]/50 bg-[#13171F] p-5" data-testid={`scene-card-${s.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.16em] text-[#C4B5FD]"><Clapperboard className="h-3.5 w-3.5" />Scene</div>
              <div className="flex gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                <button onClick={() => setEditing(s)} className="text-[#A8A090] hover:text-[#F3EBDD]" data-testid={`scene-edit-${s.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                <button onClick={() => setDeleting(s)} className="text-[#A8A090] hover:text-[#F87171]" data-testid={`scene-delete-${s.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </div>
            <div className="mt-2 font-display text-xl text-[#F3EBDD]">{s.title}</div>
            <p className="mt-1 line-clamp-3 text-sm text-[#A8A090]">{s.content || "No details yet."}</p>
            <div className="mt-3 text-[11px] text-[#A8A090]" data-testid={`scene-placements-${s.id}`}>On {s.placements} storyboard{s.placements === 1 ? "" : "s"}</div>
          </div>
        ))}
      </div>
      {editing && <SceneDialog uid={uid} scene={editing.id ? editing : null} onClose={() => setEditing(null)} onSaved={refresh} />}
      <ConfirmDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)} title="Delete this scene note?"
        description="Its placeholders are removed from current drafts. Earlier read-only drafts keep a 'missing scene' marker."
        onConfirm={async () => { await api.delete(`/scenes/${deleting.id}`); toast.success("Scene deleted"); setDeleting(null); refresh(); }} />
    </section>
  );
};
